import { $$, reducedMotion } from './dom';
import { currentLayout, onLayout } from './layout';
import { onTimeRendered } from './timer-view';

/**
 * Split-flap clock for the Flip layout. Each card has four halves; digits are shown with
 * `content: attr(data-v)` in CSS, so nothing is ever written as HTML. When a digit changes, the
 * old top half folds down over the new digit, then the new bottom half folds in (styles in
 * layouts.css). Reduced motion swaps digits instantly.
 */
interface Card {
  el: HTMLElement;
  top: HTMLElement;
  bot: HTMLElement;
  flapTop: HTMLElement;
  flapBot: HTMLElement;
}

let cards: Card[] = [];
let lastClock = '';

function half(cls: string) {
  const s = document.createElement('span');
  s.className = cls;
  return s;
}

function build() {
  cards = $$('#flip .fl-card').map((el) => {
    const c = {
      el,
      top: half('fl-half fl-top'),
      bot: half('fl-half fl-bot'),
      flapTop: half('fl-flap fl-flap-top'),
      flapBot: half('fl-flap fl-flap-bot'),
    };
    el.append(c.top, c.bot, c.flapTop, c.flapBot);
    c.flapBot.addEventListener('animationend', () => {
      c.bot.dataset.v = el.dataset.v ?? '';
      el.classList.remove('flipping');
    });
    return c;
  });
}

function setDigit(c: Card, v: string, animate: boolean) {
  const prev = c.el.dataset.v;
  if (prev === v) return;
  c.el.dataset.v = v;
  if (!animate || prev === undefined) {
    c.top.dataset.v = c.bot.dataset.v = c.flapTop.dataset.v = c.flapBot.dataset.v = v;
    c.el.classList.remove('flipping');
    return;
  }
  c.top.dataset.v = v; // revealed as the old top folds away
  c.bot.dataset.v = prev; // stays until the new bottom lands
  c.flapTop.dataset.v = prev;
  c.flapBot.dataset.v = v;
  c.el.classList.remove('flipping');
  void c.el.offsetWidth; // restart the animation
  c.el.classList.add('flipping');
}

function render(clock: string, animate: boolean) {
  const parts = clock.split(':');
  const digits = `${(parts[parts.length - 2] ?? '00').padStart(2, '0').slice(-2)}${(parts[parts.length - 1] ?? '00').slice(-2)}`;
  cards.forEach((c, i) => setDigit(c, digits[i] ?? '0', animate));
}

export function initFlip() {
  build();
  onTimeRendered((clock) => {
    lastClock = clock;
    if (currentLayout() === 'flip') render(clock, !reducedMotion() && !document.hidden);
  });
  onLayout((l) => {
    if (l === 'flip' && lastClock) render(lastClock, false);
  });
}
